import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import SPAButton from '../shared/SPAButton';
import SPACheckbox from '../shared/SPACheckbox';
import SPADialog from '../shared/SPADialog';
import RoutedSPADialog from '../shared/RoutedSPADialog';
import SPAPill from '../shared/SPAPill';
import useAssignees from './useAssignees';
import type { DropRow, KeepRow } from './useAssignees';

// AIDEV-NOTE: A class whose students are all covered is pre-ticked; one that would strand somebody is
// locked off entirely. The server re-checks both — it rejects any selected class it cannot reach and
// any that would lose a grade — so this is signposting, never the guard.
const Row = ({ checked, onToggle, row }: {
  checked: boolean;
  onToggle: (id: number) => void;
  row: DropRow;
}) => {
  const id = `unassign-class-${row.id}`;

  return (
    <li
      className="tw-flex tw-items-start tw-gap-3 tw-border-0 tw-border-b tw-border-solid tw-border-spa-border
        tw-px-1 tw-py-2"
    >
      <SPACheckbox
        checked={checked}
        className="tw-flex-1"
        disabled={!row.removable}
        id={id}
        labelClassName="tw-flex tw-flex-1 tw-flex-col tw-gap-1"
        onChange={() => onToggle(row.id)}
      >
        <span className="tw-flex tw-items-center tw-gap-2">
          <span className="tw-text-sm tw-text-spa-primary">{row.name}</span>
          <span className="tw-font-mono tw-text-xs tw-text-spa-tertiary">{row.id}</span>
          {row.removable
            ? <SPAPill tone="success">safe</SPAPill>
            : <SPAPill tone="error">would strand students</SPAPill>}
        </span>
        <span className="tw-text-sm tw-text-spa-tertiary">
          {`${row.students} students · `}
          {row.scored_students === 0 && 'none have done the work yet'}
          {row.scored_students > 0 && row.stranded_students === 0
            && `all ${row.scored_students} with a score already have it in the LMS`}
          {row.stranded_students > 0
            && `${row.stranded_students} of ${row.scored_students} with a score have it nowhere else`}
        </span>
      </SPACheckbox>
    </li>
  );
};

// Every class on the assignment is listed, so the operator sees what stays as well as what goes. One
// with a course is locked on: removing it would take the assignment away from students it can reach.
const KeptRow = ({ row }: { row: KeepRow }) => {
  const id = `keep-class-${row.id}`;

  return (
    <li
      className="tw-flex tw-items-start tw-gap-3 tw-border-0 tw-border-b tw-border-solid tw-border-spa-border
        tw-px-1 tw-py-2"
    >
      <SPACheckbox
        disabled
        className="tw-flex-1"
        id={id}
        labelClassName="tw-flex tw-flex-1 tw-flex-col tw-gap-1"
      >
        <span className="tw-flex tw-items-center tw-gap-2">
          <span className="tw-text-sm tw-text-spa-secondary">{row.name}</span>
          <span className="tw-font-mono tw-text-xs tw-text-spa-tertiary">{row.id}</span>
          <SPAPill tone="neutral">has a course</SPAPill>
        </span>
        <span className="tw-text-sm tw-text-spa-tertiary">
          {`${row.students} students · posts into ${row.course_title ?? 'a linked course'}`}
        </span>
      </SPACheckbox>
    </li>
  );
};

const AssigneePicker = () => {
  const { id: assignmentId = '' } = useParams();
  const navigate = useNavigate();
  const { preview, unassign } = useAssignees(assignmentId);
  const [picked, setPicked] = useState<number[] | null>(null);

  const rows = preview.data?.classrooms ?? [];
  // Default to what is safe, which is the answer most of the time; the operator can pare it back.
  const selected = picked ?? rows.filter(r => r.removable).map(r => r.id);

  useEffect(() => {
    if (unassign.isSuccess) navigate('..');
  }, [unassign.isSuccess]);

  const toggle = (id: number) => setPicked(
    selected.includes(id) ? selected.filter(x => x !== id) : [...selected, id],
  );

  const refusal = (unassign.error as { response?: { data?: { errors?: string[] } } })
    ?.response?.data?.errors?.[0];
  const keeping = (preview.data?.keeping ?? []).length + (rows.length - selected.length);

  return (
    <RoutedSPADialog>
      <SPADialog.Content size="md">
        <SPADialog.Header>
          <SPADialog.Title>Unassign classes the LMS cannot reach</SPADialog.Title>
          <SPADialog.Description>
            This changes who the assignment is assigned to in TCI. Nothing in the LMS is created,
            renamed or deleted, and grades already delivered stay where they are.
          </SPADialog.Description>
        </SPADialog.Header>

        <SPADialog.Body>
          {preview.data?.error != null && (
            <p className="tw-m-0 tw-text-sm tw-text-spa-error">{preview.data.error}</p>
          )}
          {refusal != null && <p className="tw-m-0 tw-mb-3 tw-text-sm tw-text-spa-error">{refusal}</p>}

          <ul className="tw-m-0 tw-list-none tw-p-0">
            {rows.map(row => (
              <Row key={row.id} checked={selected.includes(row.id)} row={row} onToggle={toggle} />
            ))}
            {(preview.data?.keeping ?? []).map(row => <KeptRow key={row.id} row={row} />)}
          </ul>

          {preview.isLoading
            ? <p className="tw-m-0 tw-text-sm tw-text-spa-secondary">Reading LMS rosters…</p>
            : (
              <p className="tw-m-0 tw-mt-3 tw-text-sm tw-text-spa-secondary">
                {selected.length === 0
                  ? 'Nothing selected.'
                  : `${selected.length} ${selected.length === 1 ? 'class comes' : 'classes come'} off; `
                    + `${keeping} ${keeping === 1 ? 'stays' : 'stay'} assigned.`}
              </p>
            )}
        </SPADialog.Body>

        <SPADialog.Footer>
          <SPADialog.Close asChild>
            <SPAButton type="button" variant="secondary">Cancel</SPAButton>
          </SPADialog.Close>
          <SPAButton
            disabled={selected.length === 0 || unassign.isLoading}
            type="button"
            variant="primary"
            onClick={() => unassign.mutate(selected)}
          >
            {selected.length === 1 ? 'Unassign 1 class' : `Unassign ${selected.length} classes`}
          </SPAButton>
        </SPADialog.Footer>
      </SPADialog.Content>
    </RoutedSPADialog>
  );
};

export default AssigneePicker;
